// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { CatalogPageModel, SpecificationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryCard from "@rpsc/App/Components/Category/CategoryCard";
import XngCarousel, { SwiperStyle } from "@rpsc/App/Components/XngCarousel/XngCarousel";
import loadFeaturedCategories from "@rpsc/App/Handlers/XNG/FeaturedCategories";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { AccordionSectionPresentationProps } from "@rpsc/MobiusOverrides/AccordionSection/AccordionSection";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import { isTouchDevice } from "@rpsc/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    return {
        catalog: catalogPageState.value,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface CustomCatalogPageModel extends CatalogPageModel {
    categorySpecifications: SpecificationModel[];
}
export interface CategorySpecificationsStyles {
    accordionSection?: AccordionSectionPresentationProps;
}

export const categorySpecificationsStyles: CategorySpecificationsStyles = {};
const styles = categorySpecificationsStyles;

const CategorySpecifications: React.FC<Props> = ({ catalog }) => {
    if (!catalog) {
        return null;
    }
    return (
        <>
            {(catalog as CustomCatalogPageModel).categorySpecifications.map((specification, index) => (
                <ManagedAccordionSection
                    key={index}
                    title={specification.name}
                    {...styles.accordionSection}
                    initialExpanded={true}
                >
                    {parse(specification.htmlContent, parserOptions)}
                </ManagedAccordionSection>
            ))}
        </>
    );
};

const categorySpecifications: WidgetModule = {
    component: connect(mapStateToProps)(CategorySpecifications),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
    },
};

export default categorySpecifications;
